/**
 * React Query hooks for CRM activities.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { CrmActivity, CrmCreateActivityInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseActivitiesOptions {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  activityType?: string;
  limit?: number;
}

function buildParams(options: UseActivitiesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.contactId) params.contactId = options.contactId;
  if (options.companyId) params.companyId = options.companyId;
  if (options.dealId) params.dealId = options.dealId;
  if (options.activityType) params.activityType = options.activityType;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useActivities(options: UseActivitiesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ activities: CrmActivity[]; total: number }>({
    queryKey: queryKeys.crm.activities.list(filters),
    queryFn: () => api.listCrmActivities(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.activities.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateActivityInput) => api.createCrmActivity(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create activity', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmActivity(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete activity', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load activities', variant: 'destructive' });
  }

  return {
    activities: query.data?.activities ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createActivity: (input: CrmCreateActivityInput) => createMutation.mutateAsync(input),
    deleteActivity: (id: string) => deleteMutation.mutateAsync(id),
  };
}
